import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiCloseCircleFill, RiRefreshLine } from 'react-icons/ri';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import { setCurrentSavedCardToken } from '../slices/currentSavedCardToken';
import Text from './Text';
import PaymentMethod from './PaymentMethod';
import StandardButton from './StandardButton';
import getCurrentSavedCardToken from '../selectors/getCurrentSavedCardToken';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { removeSavedCard } from '../slices/savedCards';
import { CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { setCvcFilled } from '../slices/cvcFilled';
import EVENTS from '../constants/events';
import { logEvent } from '../utils/analytics';
import getPaymentGatewayPublicKey from '../selectors/getPaymentGatewayPublicKey';
import { loadStripe } from '@stripe/stripe-js';
import handleCVCChange from '../utils/handleCVCChange';

const { getSelectedPaymentMethods, getEnableStripeCvcRevalidation, getGatewayType } =
  OrderingSelectors;

const { PAYMENT_GATEWAY_TYPES, PAYMENT_METHOD } = OrderingConstants;

function padCardDigits(last4: string, d = 'X'): string {
  const four = `${d}${d}${d}${d}`;
  return `${four}-${four}-${four}-${last4}`;
}

const { removePaymentGatewayToken, setCvcToken } = OrderingOperations;

interface IProps {
  savedCard: PaymentGateWayToken;
}

const PaymentMethodSavedCard: React.FC<IProps> = ({ savedCard }) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const currentSavedCardToken = useAppSelector(getCurrentSavedCardToken);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const enableCvcCheck = useAppSelector(getEnableStripeCvcRevalidation);
  const cvcFilled = useAppSelector(state => state.cvcFilled);
  const paymentGatewayPublicKey = useAppSelector(getPaymentGatewayPublicKey);
  const gatewayType = useAppSelector(getGatewayType);
  const [isRemovingToken, setIsRemovingToken] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (cvcFilled) {
      dispatch(setCvcFilled(false));
    }
  }, []);

  // eslint-disable-next-line
  const stripe = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useStripe() : null;
  // eslint-disable-next-line
  const elements = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useElements() : null;

  return (
    <PaymentMethod
      method={PAYMENT_METHOD.SAVED_CARD}
      additionalBehaviour={() => {
        loadStripe(paymentGatewayPublicKey!);
        dispatch(setCurrentSavedCardToken(savedCard.token));
      }}
      right={<Text themeKey="savedCardNumber">{padCardDigits(savedCard.displayValue)}</Text>}
      selected={() =>
        selectedPaymentMethods.some(selectedPaymentMethod => {
          if (selectedPaymentMethod.method === PAYMENT_METHOD.SAVED_CARD) {
            return true;
          }
          return false;
        }) && currentSavedCardToken === savedCard.token
      }
      subtitle={
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: '-0.7em',
            }}
          >
            <StandardButton
              invisibleContainer
              label={isRemovingToken ? t('button.removingSavedCard') : t('button.removeSavedCard')}
              themeKey="removeSavedCardButton"
              LeftIconComponent={!isRemovingToken ? RiCloseCircleFill : null}
              iconStyle={p('removeSavedCardButtonIcon', ['color', 'fontSize'])}
              onClick={async () => {
                if (window.confirm(`Remove saved card ending in ${savedCard.displayValue}?`)) {
                  setIsRemovingToken(true);
                  await dispatch(removePaymentGatewayToken({ token: savedCard.token }))
                    .unwrap()
                    .then(() => logEvent(EVENTS.REMOVE_SAVED_CARD));
                  await dispatch(removeSavedCard(savedCard));
                }
                setIsRemovingToken(false);
              }}
            />
            {isRemovingToken && (
              <div style={styles.spinnerContainer}>
                <RiRefreshLine className="spinner" style={styles.loadingSpinner} />
              </div>
            )}
          </div>
          {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE &&
            enableCvcCheck &&
            currentSavedCardToken === savedCard.token && (
              <div style={styles.contentContainer}>
                <div style={styles.input}>
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          backgroundColor: 'transparent',
                          color: p('stripeElementText', ['color']).color,
                        },
                      },
                    }}
                    onChange={event => {
                      if (event.complete) {
                        handleCVCChange({
                          stripe,
                          elements,
                          CardCvcElement,
                          onSuccess: token => dispatch(setCvcToken(token)),
                        });
                        dispatch(setCvcFilled(true));
                      } else {
                        if (cvcFilled) {
                          dispatch(setCvcFilled(false));
                        }
                        dispatch(setCvcToken(null));
                      }
                    }}
                  />
                </div>
              </div>
            )}
        </div>
      }
    />
  );
};

export default PaymentMethodSavedCard;

const styles: Styles = {
  contentContainer: {
    minHeight: 57,
  },
  input: {
    borderRadius: 4,
    border: '1px solid #c4c4c4',
  },
  spinnerContainer: {
    marginLeft: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  loadingSpinner: {
    fontSize: 16,
  },
};
